import { BEHAVIOUR_TYPES, labelForBehaviour } from './behaviourCatalog';

const fieldName = (code, fieldsByCode) => {
  if (!code) return '—';
  return fieldsByCode?.[code]?.fieldLabel || code;
};

/**
 * Produce a human-readable calculation preview string.
 */
export const humanReadableFormula = (field, fields = []) => {
  const byCode = Object.fromEntries((fields || []).map((f) => [f.fieldCode, f]));
  const cfg = field?.metadata?.businessConfig;
  if (!cfg) {
    if (field?.formulaJson) return 'Custom calculation (see Advanced)';
    return 'Manual entry';
  }

  const name = (c) => fieldName(c, byCode);
  const rate = cfg.rateValue != null && cfg.rateValue !== '' ? `${cfg.rateValue}%` : 'rate';

  switch (cfg.behaviourType) {
    case BEHAVIOUR_TYPES.MANUAL_ENTRY:
      return 'Entered manually on each RA Bill';
    case BEHAVIOUR_TYPES.REFERENCE_ONLY:
      return 'Information only — not used in bill totals';
    case BEHAVIOUR_TYPES.PREVIOUS_RA_VALUE:
      return `Value from previous RA: ${name(cfg.previousFieldCode || field.fieldCode)}`;
    case BEHAVIOUR_TYPES.SUM_OF_FIELDS:
    case BEHAVIOUR_TYPES.AUTO_TOTAL: {
      const parts = (cfg.sumFieldCodes || []).map(name);
      return parts.length ? parts.join(' + ') : 'Sum of selected fields';
    }
    case BEHAVIOUR_TYPES.SUBTRACT_FIELDS: {
      const codes = cfg.subtractFieldCodes?.length
        ? cfg.subtractFieldCodes
        : [cfg.minuendFieldCode, ...(cfg.sumFieldCodes || [])].filter(Boolean);
      if (!codes.length) return 'Subtract fields';
      return codes.map(name).join(' − ');
    }
    case BEHAVIOUR_TYPES.PERCENTAGE:
      return `${name(cfg.baseFieldCode)} × ${rate}`;
    case BEHAVIOUR_TYPES.CAPPED_PERCENTAGE: {
      const base = `${name(cfg.baseFieldCode)} × ${rate}`;
      if (cfg.capValue != null && cfg.capValue !== '') {
        return `Minimum of:\n  ${base}\n  Cap ${cfg.capValue}${cfg.capBaseFieldCode ? `% of ${name(cfg.capBaseFieldCode)}` : ''}`;
      }
      return base;
    }
    case BEHAVIOUR_TYPES.BALANCE_RECOVERY:
      return `Recover ${rate} of ${name(cfg.baseFieldCode || cfg.originalAdvanceFieldCode)}, capped at outstanding ${name(cfg.previousOutstandingFieldCode || cfg.originalAdvanceFieldCode)}`;
    case BEHAVIOUR_TYPES.QUANTITY_RATE:
      return `${name(cfg.quantityFieldCode) || cfg.quantityValue || 'Qty'} × ${name(cfg.rateFieldCode) || cfg.rateValue || 'Rate'}`;
    case BEHAVIOUR_TYPES.CURRENT_MINUS_PREVIOUS:
      return `${name(cfg.baseFieldCode || field.fieldCode)} − Previous`;
    case BEHAVIOUR_TYPES.CONDITIONAL:
      return 'Conditional calculation (see Calculation tab)';
    case BEHAVIOUR_TYPES.CUSTOM_FORMULA:
      return describeAst(field.formulaJson, byCode) || 'Custom formula';
    default:
      return labelForBehaviour(cfg.behaviourType);
  }
};

export const describeAst = (node, byCode = {}) => {
  if (!node) return '';
  if (typeof node === 'number') return String(node);
  if (node.type === 'CONSTANT') return String(node.value);
  if (node.type === 'FIELD') return byCode[node.fieldCode]?.fieldLabel || node.fieldCode;
  if (node.type === 'PREVIOUS_RA_FIELD') {
    return `Previous(${byCode[node.fieldCode]?.fieldLabel || node.fieldCode})`;
  }
  const op = String(node.operator || '').toUpperCase();
  const args = (node.arguments || []).map((a) => describeAst(a, byCode));
  const join = (sym) => args.join(` ${sym} `);
  switch (op) {
    case 'ADD':
    case 'SUM':
      return join('+');
    case 'SUBTRACT':
      return join('−');
    case 'MULTIPLY':
      return join('×');
    case 'DIVIDE':
      return join('÷');
    case 'PERCENTAGE':
      return args.length >= 2 ? `${args[0]} × ${args[1]}%` : 'Percentage';
    case 'MIN':
      return `Minimum of: ${args.join(', ')}`;
    case 'MAX':
      return `Maximum of: ${args.join(', ')}`;
    case 'IF':
      return 'If condition then result else alternate';
    default:
      return op ? `${op}(${args.join(', ')})` : '';
  }
};

/** Collect same-bill FIELD refs from AST (for Depends On display). */
export const collectFieldDeps = (node, set = new Set()) => {
  if (!node || typeof node !== 'object') return set;
  if (node.type === 'FIELD' && node.fieldCode) set.add(node.fieldCode);
  (node.arguments || []).forEach((a) => collectFieldDeps(a, set));
  ['left', 'right', 'condition', 'then', 'else'].forEach((k) => {
    if (node[k]) collectFieldDeps(node[k], set);
  });
  return set;
};

export const buildDependencyMaps = (fields) => {
  const dependsOn = {};
  const usedBy = {};
  (fields || []).forEach((f) => {
    const deps = [...collectFieldDeps(f.formulaJson)];
    dependsOn[f.fieldCode] = deps;
    deps.forEach((d) => {
      if (!usedBy[d]) usedBy[d] = [];
      usedBy[d].push(f.fieldCode);
    });
  });
  return { dependsOn, usedBy };
};
